import {trapFocus, removeTrapFocus} from '@theme/focus';

class QuickAddToCartModal extends HTMLElement {
  constructor() {
    super();

    // Get modal elements
    this.modal = document.querySelector('.rs-quick-add-to-cart-modal');
    // Prefer the standalone page mask; nested masks live inside other rs-hidden modals
    // and never paint a backdrop when toggled.
    this.modalMask =
      Array.from(document.querySelectorAll('.rs-modal-mask')).find((el) => !el.closest('.rs-modal')) ||
      document.querySelector('.rs-modal-mask');
    this.triggerButtons = document.querySelectorAll('.rs-quick-add-to-cart-modal--trigger');
    this.closeButtons = this.modal?.querySelectorAll('.rs-modal-close, .rs-modal__close-button');
    this.mainContent = document.querySelector('main') || document.body;
    this.quantitiesWillIncreaseBanner = this.modal?.querySelector('.rs-quick-add-to-cart-modal__quantities-will-increase');
    this.quantitiesWillIncreaseBannerText = this.quantitiesWillIncreaseBanner?.querySelector('.rs-modal__paragraph');
    this.quantitiesWillIncreaseTable = this.modal?.querySelector('.rs-modal__atc-table');
    this.quantitiesWillIncreaseTableBody = this.modal?.querySelector('.rs-modal__atc-table-body');
    this.quantitiesWillIncreaseShowSkusButton = this.modal?.querySelector('.rs-skus-button');
    this.successFooter = this.modal?.querySelector('.rs-modal__footer--success');
    this.defaultFooter = this.modal?.querySelector('.rs-modal__footer--default');
    this.triggerButton = null;
    this.addToCartButton = null;
    this.cart = null;
    this.pendingCartSectionRefresh = false;

    this.init();
  }

  async init() {
    this.cart = await this.getCart();

    if (!this.modal) return;

    // Open modal when trigger button is clicked
    this.triggerButtons.forEach((button) => {
      button.addEventListener('click', this.openModal.bind(this, button));
    });

    // Close modal when close button is clicked
    this.closeButtons?.forEach((button) => {
      button.addEventListener('click', this.closeModal.bind(this));
    });

    // Close modal when clicking the backdrop (mask sits under the full-screen modal)
    this.modalMask?.addEventListener('click', (e) => {
      if (e.target === this.modalMask) {
        this.closeModal();
      }
    });
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Close modal on Escape key
    this.addEventListener('keydown', this.handleKeyDown.bind(this));

    // Add to cart button click handler
    this.addToCartButton = this.querySelector('.rs-button-primary');
    this.addToCartButton?.addEventListener('click', this.addToCart.bind(this));

    // Show SKUs button click handler
    this.quantitiesWillIncreaseShowSkusButton?.addEventListener('click', this.showSkus.bind(this));
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      if (response?.status !== 200) return;

      const cartData = await response.json();
      return cartData;
    } catch (error) {
      console.error('Error getting cart', error);
    }
  }

  /**
   * Handle keyboard events for the modal
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape' && this.modal && !this.modal.classList.contains('rs-hidden')) {
      event.preventDefault();
      this.closeModal();
    }
  }

  getPageContext() {
    const isOrderDetailsPage = !!document.querySelector('.order-details-page');
    const hasOrderDetailsSelection = !!document.querySelector('.rs-table-body__row.rs-selected');
    const hasQuickOrderSelection = !!document.querySelector('.rs-table-body__row--selected');

    return {isOrderDetailsPage, hasOrderDetailsSelection, hasQuickOrderSelection};
  }

  /**
   * @param {Element} row
   * @param {{ isOrderDetailsPage: boolean; hasOrderDetailsSelection: boolean; hasQuickOrderSelection: boolean; }} context
   */
  isRowEligibleForAdd(row, context) {
    if (row.querySelector('.rs-error')) return false;

    const materialInput = row.querySelector('.rs-material-number-input-wrapper input');
    const quantityInput = row.querySelector('.rs-quantity-input-wrapper input');
    if (!(materialInput instanceof HTMLInputElement) || !(quantityInput instanceof HTMLInputElement)) return false;
    if (!materialInput.value || !quantityInput.value) return false;

    // Order details page skus with a null product variant id are not added to the cart, so we need to skip them
    if (materialInput.dataset.productVariantId === 'null' && context.isOrderDetailsPage) return false;

    if (context.isOrderDetailsPage) {
      if (context.hasOrderDetailsSelection && !row.classList.contains('rs-selected')) return false;
    } else if (context.hasQuickOrderSelection && !row.classList.contains('rs-table-body__row--selected')) {
      return false;
    }

    return true;
  }

  getEligibleRows() {
    const context = this.getPageContext();
    const eligibleRows = [];

    for (const row of document.querySelectorAll('.rs-table-body__row')) {
      if (this.isRowEligibleForAdd(row, context)) eligibleRows.push(row);
    }

    return eligibleRows;
  }

  /**
   * Leaf splits held on a cart line as `_requested_deliveries` — keep in sync with `<cart-table>`.
   * Shopify can hand the property back as a JSON string, and splits may be nested in batches.
   * @param {{ properties?: Record<string, unknown> } | null | undefined} lineItem
   * @returns {Array<Record<string, any>>}
   */
  requestedDeliveryLeaves(lineItem) {
    let raw = lineItem?.properties?._requested_deliveries;
    if (raw == null) return [];

    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];

    return /** @type {Array<Record<string, any>>} */ (
      raw
        .flat(Infinity)
        .map((delivery) => {
          if (!delivery || typeof delivery !== 'object' || Array.isArray(delivery)) return null;
          const rec = /** @type {Record<string, any>} */ (delivery);
          const date = String(rec._delivery_date ?? rec.d ?? '').trim();
          if (!date) return null;
          const quantity = rec.quantity ?? rec.q;
          return {
            ...rec,
            _delivery_date: date,
            quantity: quantity != null && !Number.isNaN(Number(quantity)) ? Number(quantity) : rec.quantity,
          };
        })
        .filter((delivery) => delivery != null)
    );
  }

  /**
   * A line with split deliveries stores every split in `_requested_deliveries` and carries their
   * summed quantity, so merging a dateless add into it would desync the splits. Those lines must
   * get a separate line item instead.
   * @param {{ properties?: Record<string, unknown> } | null | undefined} lineItem
   * @returns {boolean}
   */
  hasRequestedDeliveries(lineItem) {
    return this.requestedDeliveryLeaves(lineItem).length > 0;
  }

  /**
   * Uses compact `{q,d}` keys when verbose JSON would exceed Shopify's 255-character property cap.
   * @param {Array<Record<string, any>>} leaves
   * @returns {string}
   */
  compactRequestedDeliveriesForShopify(leaves) {
    const normalized = (Array.isArray(leaves) ? leaves : [])
      .map((l) => {
        if (!l || typeof l !== 'object' || Array.isArray(l)) return null;
        const rec = {...l};
        const quantity = Number(rec.quantity ?? rec.q);
        const date = String(rec._delivery_date ?? rec.d ?? '').trim();
        delete rec.quantity;
        delete rec._delivery_date;
        delete rec.q;
        delete rec.d;
        if (!date || !Number.isFinite(quantity) || quantity <= 0) return null;
        return {quantity, _delivery_date: date, rest: rec};
      })
      .filter((l) => l != null);

    const full = normalized.map((l) => ({quantity: l.quantity, _delivery_date: l._delivery_date, ...l.rest}));
    const payload =
      JSON.stringify(full).length <= 255
        ? full
        : normalized.map((l) => ({q: l.quantity, d: l._delivery_date, ...l.rest}));
    return JSON.stringify(payload);
  }

  /**
   * Canonical key so `04/01/2026`, `4/1/2026`, and `2026-04-01` compare equal — mirrors `<cart-updates>`.
   * @param {unknown} value
   * @returns {string}
   */
  normalizeDeliveryDateMatchKey(value) {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';

    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const us = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (us && us[1] && us[2] && us[3]) {
      return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
    }

    return trimmed.toLowerCase();
  }

  /**
   * Cart line for this variant that already requests `deliveryDate`, either as its primary
   * `_delivery_date` or as one of its splits, so the add bumps that quantity instead of opening a
   * second line for a date the cart already carries.
   * @param {Array<{ variant_id: number; properties?: Record<string, unknown>; }>} cartItems
   * @param {number} variantId
   * @param {string} deliveryDate
   */
  findLineItemWithRequestedDelivery(cartItems, variantId, deliveryDate) {
    const target = this.normalizeDeliveryDateMatchKey(deliveryDate);
    if (!target) return null;

    return (
      cartItems.find((item) => {
        if (item.variant_id !== variantId) return false;
        if (this.normalizeDeliveryDateMatchKey(item.properties?._delivery_date) === target) return true;

        return this.requestedDeliveryLeaves(item).some(
          (leaf) => this.normalizeDeliveryDateMatchKey(leaf._delivery_date) === target
        );
      }) || null
    );
  }

  /**
   * Quantity a cart line currently holds for one requested delivery date: the matching split when
   * the line is split, otherwise the whole line.
   * @param {{ quantity?: unknown; properties?: Record<string, unknown>; } | null | undefined} lineItem
   * @param {string} deliveryDate
   * @returns {number}
   */
  requestedDeliveryQuantity(lineItem, deliveryDate) {
    const target = this.normalizeDeliveryDateMatchKey(deliveryDate);
    const matchedLeaf = target
      ? this.requestedDeliveryLeaves(lineItem).find(
          (leaf) => this.normalizeDeliveryDateMatchKey(leaf._delivery_date) === target
        )
      : null;

    return matchedLeaf ? Number(matchedLeaf.quantity) || 0 : Number(lineItem?.quantity) || 0;
  }

  /**
   * `/cart/change.js` quantity patch for a line that already requests `deliveryDate`. Split lines
   * keep a quantity per split and carry the sum as the Shopify line quantity, so the matching split
   * is bumped and the line total recomputed from the splits.
   * @param {{ quantity?: unknown; properties?: Record<string, unknown>; }} lineItem
   * @param {string} deliveryDate
   * @param {number} addedQuantity
   * @returns {{ quantity: number; properties?: Record<string, unknown>; }}
   */
  buildRequestedDeliveryQuantityPatch(lineItem, deliveryDate, addedQuantity) {
    const lineTotal = {quantity: (Number(lineItem?.quantity) || 0) + addedQuantity};

    const leaves = this.requestedDeliveryLeaves(lineItem);
    if (leaves.length === 0) return lineTotal;

    const target = this.normalizeDeliveryDateMatchKey(deliveryDate);
    let matched = false;
    const updated = leaves.map((leaf) => {
      const leafQuantity = Number(leaf.quantity) || 0;
      if (matched || this.normalizeDeliveryDateMatchKey(leaf._delivery_date) !== target) {
        return {...leaf, quantity: leafQuantity};
      }

      matched = true;
      return {...leaf, quantity: leafQuantity + addedQuantity};
    });

    // Carts saved before the primary split was stored keep splits 2+ only, so an unmatched date
    // belongs to the primary, whose quantity is the line total minus those splits.
    if (!matched) return lineTotal;

    return {
      quantity: updated.reduce((sum, leaf) => sum + (Number(leaf.quantity) || 0), 0),
      properties: {_requested_deliveries: this.compactRequestedDeliveriesForShopify(updated)},
    };
  }

  /**
   * Requested-delivery input for a row. Flatpickr keeps the submitted value on `input.flatpickr-input`
   * and shows a separate alt input, so the wrapper can hold more than one input.
   * @param {Element} row
   */
  findDeliveryDateInput(row) {
    const wrapper = row.querySelector('.rs-date-input-wrapper') || row.querySelector('.quick-order__delivery-date');

    return (
      wrapper?.querySelector('input.flatpickr-input') ||
      wrapper?.querySelector('input[type="date"].rs-input-type__date:not(.flatpickr-input)') ||
      wrapper?.querySelector('input') ||
      row.querySelector('.rs-date-input-wrapper input')
    );
  }

  /**
   * Date shown for a requested-delivery input, preferring Flatpickr's visible alt input so the
   * value matches the format stored on the cart line.
   * @param {Element | null | undefined} deliveryDateInput
   * @returns {string}
   */
  resolveDeliveryDateValue(deliveryDateInput) {
    const fp = deliveryDateInput ? /** @type {any} */ (deliveryDateInput)._flatpickr : undefined;

    return (
      (fp?.altInput?.value && String(fp.altInput.value).trim()) ||
      (fp?.input?.value && String(fp.input.value).trim()) ||
      (deliveryDateInput instanceof HTMLInputElement && deliveryDateInput.value?.trim()) ||
      ''
    );
  }

  /**
   * @param {Array<{ variant_id: number; quantity: number; properties?: Record<string, unknown>; }>} cartItems
   * @param {Element[]} eligibleRows
   */
  findDuplicateItems(cartItems, eligibleRows) {
    const duplicateItems = [];

    for (const row of eligibleRows) {
      const materialInput = row.querySelector('.rs-material-number-input-wrapper input');
      const quantityInput = row.querySelector('.rs-quantity-input-wrapper input');
      if (!(materialInput instanceof HTMLInputElement) || !(quantityInput instanceof HTMLInputElement)) continue;

      const productVariantId = materialInput.dataset.productVariantId;
      if (!productVariantId || productVariantId === 'null' || productVariantId === 'undefined') continue;

      const variantId = parseInt(productVariantId, 10);
      const incomingDate = this.resolveDeliveryDateValue(this.findDeliveryDateInput(row));

      // Mirror the merge rules in `addToCart` so the banner previews the quantities that really change.
      const cartItem = incomingDate
        ? this.findLineItemWithRequestedDelivery(cartItems, variantId, incomingDate)
        : cartItems.find((item) => item.variant_id === variantId && !this.hasRequestedDeliveries(item));
      if (!cartItem) continue;

      duplicateItems.push({
        sku_number: materialInput.value,
        cart_quantity: this.requestedDeliveryQuantity(cartItem, incomingDate),
        list_quantity: quantityInput.value,
      });
    }

    return duplicateItems;
  }

  resetQuantitiesWillIncreaseBanner() {
    this.quantitiesWillIncreaseBanner?.classList.add('hidden');

    if (this.quantitiesWillIncreaseTableBody) this.quantitiesWillIncreaseTableBody.innerHTML = '';

    this.quantitiesWillIncreaseShowSkusButton?.classList.add('rs-skus-hidden');
    if (this.quantitiesWillIncreaseShowSkusButton) {
      this.quantitiesWillIncreaseShowSkusButton.textContent = window.__t('show_skus');
    }
  }

  /**
   * Activate the focus trap after the modal has been painted so its visible
   * controls are available to the shared focus utility.
   */
  scheduleFocusTrap() {
    if (!this.modal) return;

    const modalContent = this.modal.querySelector('.rs-modal__content');
    const initialFocus = this.modal.querySelector('.rs-modal__close-button');
    if (!(modalContent instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.modal || this.modal.classList.contains('rs-hidden')) return;

        trapFocus(modalContent, {
          initialFocus: initialFocus instanceof HTMLElement ? initialFocus : null,
        });
      });
    });
  }

  /**
   * @param {number} duplicateItemsCount
   */
  renderQuantitiesWillIncreaseBanner(duplicateItemsCount) {
    const message =
      duplicateItemsCount === 1
        ? window.__t('quantities_will_increase_one')
        : window.__t('quantities_will_increase', {count: duplicateItemsCount});

    this.quantitiesWillIncreaseBanner?.classList.remove('hidden');

    if (this.quantitiesWillIncreaseBannerText) {
      this.quantitiesWillIncreaseBannerText.innerHTML = `
        <svg class="rs-quick-add-to-cart-modal__quantities-will-increase-icon" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
          <path d="M10.3333 2.00001C5.73096 2.00001 2 5.73097 2 10.3333C2 14.9357 5.73096 18.6667 10.3333 18.6667C14.9357 18.6667 18.6667 14.9357 18.6667 10.3333C18.6667 5.73097 14.9357 2.00001 10.3333 2.00001Z" stroke="black" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.3346 13.2782V9.8338" stroke="black" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.3346 6.38933H10.3263" stroke="black" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${message}`;
    }
  }

  /**
   * Open the modal
   * @param {HTMLElement} button - The trigger button that opened the modal
   */
  async openModal(button) {
    if (!this.modal) return;

    // Store reference to trigger button for focus return
    this.triggerButton = button;

    this.resetQuantitiesWillIncreaseBanner();

    // Hide background content from screen readers
    this.mainContent.setAttribute('aria-hidden', 'true');
    document.body.classList.add('rs-overflow-hidden');

    // Show modal to screen readers
    this.modal.classList.remove('rs-hidden');
    this.modal.setAttribute('aria-hidden', 'false');

    // Show modal mask
    this.modalMask?.classList.remove('rs-hidden');

    // Trap focus after the modal layout settles.
    this.scheduleFocusTrap();

    const eligibleRows = this.getEligibleRows();

    // Update the amount of skus in the modal paragraph
    const skuCountContainer = this.modal.querySelector('.rs-quick-add-to-cart-modal__content .rs-modal__paragraph');
    if (skuCountContainer) {
      skuCountContainer.innerHTML = window.__t('about_to_add_skus_to_cart', {count: eligibleRows.length});
    }

    this.cart = await this.getCart();
    const duplicateItems = this.findDuplicateItems(this.cart?.items || [], eligibleRows);

    if (duplicateItems.length > 0) {
      this.renderQuantitiesWillIncreaseBanner(duplicateItems.length);

      if (this.quantitiesWillIncreaseTableBody) {
        this.quantitiesWillIncreaseTableBody.innerHTML = duplicateItems
          .map((item) => this.createLineItem(item))
          .join('');
      }
    }

    // Show default footer
    this.defaultFooter?.classList.remove('hidden');

    // Hide success footer
    this.successFooter?.classList.add('hidden');
  }

  /**
   * @param {{ sku_number: any; cart_quantity: any; list_quantity: any; }} item
   */
  createLineItem(item) {
    if (!item) return '';
    const {sku_number, cart_quantity, list_quantity} = item;

    return `<div class="rs-modal__atc-table-body-row rs-flex rs-gap-16 rs-pd-x-8">
          <div class="rs-modal__atc-table-cell rs-pd-x-12 rs-pd-y-8" style="width: 250px;">
            <div id="" class="rs-text text-primary text-small text- weight-500 rs-hide-desktop">SKU / Customer Part #:</div>
            <div id="" class="rs-text text-primary text-small text- weight-400 rs-sku">${sku_number}</div>
          </div>
          <div class="rs-modal__atc-table-cell rs-pd-x-12 rs-pd-y-8" style="width: 150px;">
            <div id="" class="rs-text text-primary text-small text- weight-500 rs-hide-desktop">Qty In List</div>
            <div id="" class="rs-text text-primary text-small text- weight-400 rs-qty-list">${list_quantity}</div>
          </div>
          <div class="rs-modal__atc-table-cell rs-pd-x-12 rs-pd-y-8" style="width: 150px;">
            <div id="" class="rs-text text-primary text-small text- weight-500 rs-hide-desktop">Current Qty In Cart</div>
            <div id="" class="rs-text text-primary text-small text- weight-400 rs-qty-cart">${cart_quantity}</div>
          </div>
          <div class="rs-modal__atc-table-cell rs-modal__atc-table-cell-new-total rs-pd-x-12 rs-pd-y-8">
            <div id="" class="rs-text text-primary text-small text- weight-600 rs-hide-desktop">New Total</div><div id="" class="rs-text text-primary text-small text- weight-400 rs-total">${parseInt(cart_quantity, 10) + parseInt(list_quantity, 10)}</div>
          </div>
        </div>`;
  }

  showSkus() {
    this.quantitiesWillIncreaseShowSkusButton?.classList.toggle('rs-skus-hidden');

    if (!this.quantitiesWillIncreaseShowSkusButton?.classList.contains('rs-skus-hidden')) {
      this.quantitiesWillIncreaseShowSkusButton.textContent = window.__t('hide_skus');
    } else {
      this.quantitiesWillIncreaseShowSkusButton.textContent = window.__t('show_skus');
    }
  }

  /**
   * Close the modal
   */
  closeModal() {
    if (!this.modal) return;

    // Remove focus trap
    removeTrapFocus();

    // Show background content to screen readers
    this.mainContent.setAttribute('aria-hidden', 'false');
    document.body.classList.remove('rs-overflow-hidden');

    // Hide modal from screen readers
    this.modal.classList.add('rs-hidden');
    this.modal.setAttribute('aria-hidden', 'true');

    // Hide modal mask
    this.modalMask?.classList.add('rs-hidden');

    // Return focus to trigger button
    if (this.triggerButton) {
      this.triggerButton.focus();
      this.triggerButton = null;
    }

    this.resetQuantitiesWillIncreaseBanner();

    // Run the deferred cart section refresh only after the dialog is closed,
    // so success actions (Back to Quick Order / View Cart) remain usable.
    if (this.pendingCartSectionRefresh) {
      this.pendingCartSectionRefresh = false;
      document.dispatchEvent(new CustomEvent('cart:updated', {detail: {source: 'quick-order-modal'}}));
    }
  }

  /**
   * Add to cart
   */
  async addToCart() {
    // Disable button during processing
    this.addToCartButton.disabled = true;
    this.addToCartButton.setAttribute('aria-busy', 'true');
    const translationEl = document.getElementById('rs-quick-add-to-cart-modal-translation-data');
    const addingItemsToCartLabel = translationEl?.dataset?.addingItemsToCart || 'Adding items to cart...';

    // Update modal messaging to indicate that items are being added to cart
    const modalTitle = this.querySelector('.rs-modal__paragraph');
    if (modalTitle && modalTitle instanceof HTMLElement) {
      modalTitle.innerHTML = `
      <div class="rs-modal__loading-ellipsis"><span></span></div>
      ${addingItemsToCartLabel}`;

      // Hide the quantities will increase banner
      this.quantitiesWillIncreaseBanner?.classList.add('hidden');

      this.classList.add('rs-adding-to-cart');
    }

    try {
      // Collect eligible rows first, prefetch RTP, then build cart lines with `_price_override`.
      const tableRows = document.querySelectorAll('.rs-table-body__row');
      /** @type {Array<{ row: Element, materialInput: HTMLInputElement, quantityInput: HTMLInputElement, deliveryDateInput: Element | null, materialNumber: string, quantity: number, productVariantId: string, sku: string }>} */
      const candidates = [];

      for (const row of tableRows) {
        if (row.querySelector('.rs-error')) continue;

        const materialInput = row.querySelector('.rs-material-number-input-wrapper input');
        const quantityInput = row.querySelector('.rs-quantity-input-wrapper input');
        const deliveryDateInput = this.findDeliveryDateInput(row);

        if (!materialInput || !quantityInput || !(materialInput instanceof HTMLInputElement) || !(quantityInput instanceof HTMLInputElement)) continue;

        let materialNumber = null;
        const customerPartNumberSku = materialInput.nextElementSibling?.querySelector(`.customer-part-number-suggestion[data-part-number="${materialInput.value.trim()}"]`);
        if (materialInput.getAttribute('data-selected-sku')) {
          materialNumber = materialInput.getAttribute('data-selected-sku');
        } else if (customerPartNumberSku) {
          materialNumber = customerPartNumberSku.dataset.sku;
        } else {
          materialNumber = materialInput.value?.trim();
        }
        
        const quantity = parseInt(quantityInput.value, 10) || 0;
        if (!materialNumber || quantity <= 0) continue;

        const productVariantId = materialInput.dataset.productVariantId;
        if (!productVariantId || productVariantId === 'null' || productVariantId === 'undefined') continue;

        if (document.querySelector('.order-details-page')) {
          const hasSelectedRows = document.querySelector('.rs-table-body__row.rs-selected');
          if (hasSelectedRows && !row.classList.contains('rs-selected')) continue;
        } else {
          const hasSelectedRows = document.querySelector('.rs-table-body__row--selected');
          if (hasSelectedRows && !row.classList.contains('rs-table-body__row--selected')) continue;
        }

        const sku =
          materialNumber ||
          (row instanceof HTMLElement && row.dataset.materialNumber?.trim()) ||
          row.querySelector('[data-sku]')?.dataset?.sku?.trim() ||
          '';

        candidates.push({
          row,
          materialInput,
          quantityInput,
          deliveryDateInput,
          materialNumber,
          quantity,
          productVariantId,
          sku,
        });
      }

      if (candidates.length === 0) {
        if (modalTitle && modalTitle instanceof HTMLElement) {
          modalTitle.innerHTML = window.__t('about_to_add_skus_to_cart', { count: 0 });
        }
        this.classList.remove('rs-adding-to-cart');
        if (this.addToCartButton instanceof HTMLButtonElement) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.removeAttribute('aria-busy');
        }
        return;
      }

      // Soft cart ceiling (line items / SKUs).
      const maxCartLines = typeof window.getMaxCartLineItems === 'function'
        ? window.getMaxCartLineItems()
        : (window.RS_MAX_CART_LINE_ITEMS || 200);
      if (candidates.length > maxCartLines) {
        if (this.addToCartButton instanceof HTMLButtonElement) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.removeAttribute('aria-busy');
        }
        if (modalTitle && modalTitle instanceof HTMLElement) {
          modalTitle.textContent =
            (typeof window.cartLineLimitMessage === 'function'
              ? window.cartLineLimitMessage(maxCartLines)
              : window.__t?.('cart_line_item_limit', { count: maxCartLines })) ||
            `Your cart is limited to ${maxCartLines} SKUs. Please remove excess items before adding more.`;
        }
        this.classList.remove('rs-adding-to-cart');
        return;
      }

      // Fetch realtime prices before add so `_price_override` is available on every line.
      /** @type {Map<string, string>} */
      let pricesBySku = new Map();
      /** @type {Set<string>} */
      let unpricedSkus = new Set();
      if (typeof window.ensureRealtimePricesForAddToCart === 'function') {
        try {
          const prefetch = await window.ensureRealtimePricesForAddToCart(
            candidates.map((c) => ({
              sku: c.sku,
              quantity: c.quantity,
              variantId: c.productVariantId,
            })),
          );
          pricesBySku = prefetch?.pricesBySku || pricesBySku;
          unpricedSkus = prefetch?.unpricedSkus || unpricedSkus;
        } catch (rtpError) {
          console.warn('[RTP] quick-add price prefetch failed', rtpError);
        }
      }

      // The cart blocks checkout on any line without a price (SAP will not price an inactive SKU),
      // so nothing is added until those SKUs are removed.
      const unpricedCandidates = candidates.filter((c) => c.sku && unpricedSkus.has(c.sku));
      if (unpricedCandidates.length) {
        unpricedCandidates.forEach(({ materialInput }) => {
          const wrapper = materialInput.closest('.rs-material-number-input-wrapper');
          const errorText = wrapper?.querySelector('.rs-error__message-text, .rs-input__error span');
          if (errorText) errorText.textContent = window.__t('price_unavailable');
          delete materialInput.dataset.productVariantId;
          wrapper?.classList.add('rs-error');
        });
        if (modalTitle && modalTitle instanceof HTMLElement) {
          modalTitle.textContent = window.__t('skus_blocked_no_price', {
            skus: [...new Set(unpricedCandidates.map((c) => c.sku))].join(', '),
          });
        }
        this.classList.remove('rs-adding-to-cart');
        if (this.addToCartButton instanceof HTMLButtonElement) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.removeAttribute('aria-busy');
        }
        return;
      }

      const items = [];
      for (const candidate of candidates) {
        const {
          row,
          deliveryDateInput,
          quantity,
          productVariantId,
          sku,
        } = candidate;
        const deliveryDateValue = this.resolveDeliveryDateValue(deliveryDateInput);

        const lineProperties = {};
        if (sku) {
          lineProperties._sku = sku;
        }
        if (deliveryDateValue) {
          lineProperties._delivery_date = deliveryDateValue;
        }

        // Prefer the prefetch map, then DOM/cache helpers — `/cart/change` is not
        // patched by the global `/cart/add` fetch hook, so overrides must be set here.
        const prefetchedPrice = sku ? pricesBySku.get(sku) : undefined;
        if (prefetchedPrice) {
          lineProperties._price_override = String(prefetchedPrice);
          lineProperties._price_override_reason = 'realtime_price';
        }

        items.push({
          id: productVariantId,
          quantity,
          // BPM-5360: merge _delivery_date with realtime price + plant line properties at add-to-cart.
          properties: window.mergeAddToCartLineProperties?.(
            row,
            lineProperties,
            { variantId: productVariantId, sku: sku || undefined }
          ) || window.mergePriceOverrideLineProperties?.(
            row,
            lineProperties,
            { variantId: productVariantId, sku: sku || undefined }
          ) || lineProperties,
        });
      }

      // Fetch current cart state to detect existing line items for the same variant.
      // Shopify keys line items on their properties, so a new add whose delivery date differs from
      // an existing line's — including an add with no date at all — lands as a duplicate.
      // We prevent that by incrementing the existing item via cart/change.js instead.
      const cartState = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const cartItems = cartState.items || [];

      const itemsToAdd = [];
      /** @type {Array<{ id: string; quantity: number; properties: Record<string, any>; }>} */
      const changeRequests = [];

      for (const item of items) {
        const variantId = parseInt(item.id, 10);
        const incomingDate = item.properties?._delivery_date || '';
        // A dated add belongs on the line already requesting that same date, primary or split.
        // A dateless add merges into a plain line only: lines carrying `_requested_deliveries`
        // track a quantity per split and must not silently absorb it.
        const existing = incomingDate
          ? this.findLineItemWithRequestedDelivery(cartItems, variantId, incomingDate)
          : cartItems.find(ci => ci.variant_id === variantId && !this.hasRequestedDeliveries(ci));

        if (existing) {
          // Merge into the existing line item regardless of its other properties.
          // Always re-resolve `_price_override` so qty merges still carry the realtime price
          // (the `/cart/add` fetch hook does not run for `/cart/change`).
          const itemSku = item.properties?._sku;
          const resolvedOverrides =
            (typeof window.getPriceOverrideLineProperties === 'function'
              && window.getPriceOverrideLineProperties(null, {
                variantId,
                sku: itemSku || undefined,
              }))
            || {};
          const prefetchedPrice = itemSku ? pricesBySku.get(String(itemSku)) : undefined;
          /** @type {Record<string, string>} */
          const priceOverrides = {...resolvedOverrides};
          if (prefetchedPrice && !priceOverrides._price_override) {
            priceOverrides._price_override = String(prefetchedPrice);
            priceOverrides._price_override_reason = 'realtime_price';
          }

          /** @type {{ quantity: number; properties?: Record<string, unknown>; }} */
          const quantityPatch = incomingDate
            ? this.buildRequestedDeliveryQuantityPatch(existing, incomingDate, item.quantity)
            : {quantity: existing.quantity + item.quantity};

          changeRequests.push({
            id: existing.key,
            quantity: quantityPatch.quantity,
            properties: {
              ...(existing.properties || {}),
              ...Object.fromEntries(
                Object.entries(item.properties || {}).filter(
                  // The line already holds the right primary date: a dated add either matched it or
                  // matched one of the splits, where overwriting it would move the primary's date.
                  ([key, value]) => key !== '_delivery_date' && value != null && value !== ''
                )
              ),
              ...Object.fromEntries(
                Object.entries(priceOverrides).filter(([, value]) => value != null && value !== '')
              ),
              ...(quantityPatch.properties || {}),
            },
          });
        } else {
          itemsToAdd.push(item);
        }
      }

      if (cartItems.length + itemsToAdd.length > maxCartLines) {
        if (this.addToCartButton instanceof HTMLButtonElement) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.removeAttribute('aria-busy');
        }
        if (modalTitle && modalTitle instanceof HTMLElement) {
          modalTitle.textContent =
            (typeof window.cartLineLimitMessage === 'function'
              ? window.cartLineLimitMessage(maxCartLines)
              : window.__t?.('cart_line_item_limit', { count: maxCartLines })) ||
            `Your cart is limited to ${maxCartLines} SKUs. Please remove excess items before adding more.`;
        }
        this.classList.remove('rs-adding-to-cart');
        return;
      }

      // Run the quantity merges one at a time, in row order. Concurrent cart writes
      // are applied in whatever order the server receives them, which shuffles the
      // resulting line items relative to the rows the shopper sees.
      for (const changeRequest of changeRequests) {
        const changeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(changeRequest),
        });
        if (!changeResponse.ok) throw new Error(`Failed to update cart item (HTTP ${changeResponse.status})`);
      }

      // Shopify sorts cart line items newest-first (reverse chronological) as of API
      // 2023-10, so a batch posted in row order renders bottom-up on the cart page.
      // Post it reversed to land the new lines in the same order as the form rows.
      const response = itemsToAdd.length > 0
        ? await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({items: [...itemsToAdd].reverse()}),
          })
        : { ok: true, status: 200 };

      if (response?.status !== 200) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `Failed to add to cart (HTTP ${response.status})`);
      }

      const translationEl = document.getElementById('rs-quick-add-to-cart-modal-translation-data');
      const skusHaveBeenAddedToCartLabel = translationEl?.dataset?.skusHaveBeenAddedToCart || 'SKUs have been added to your cart.';


      // Update paragraph to show success message
      if (modalTitle && modalTitle instanceof HTMLElement) {
        modalTitle.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clip-path="url(#clip0_1614_70992)">
            <mask id="mask0_1614_70992" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <path d="M12 0C18.6274 0 24 5.37259 24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C3.0924e-06 5.37261 5.37262 4.63876e-05 12 0ZM12 1.6748C6.29738 1.67485 1.67481 6.29737 1.6748 12C1.6748 17.7026 6.29738 22.3251 12 22.3252C17.7027 22.3252 22.3252 17.7027 22.3252 12C22.3252 6.29734 17.7027 1.6748 12 1.6748ZM14.7568 9.17578C15.0838 8.84886 15.6135 8.84891 15.9404 9.17578C16.2674 9.50273 16.2674 10.0324 15.9404 10.3594L11.4756 14.8242C11.1486 15.1512 10.6189 15.1512 10.292 14.8242L8.05957 12.5918C7.73262 12.2648 7.73262 11.7352 8.05957 11.4082C8.36601 11.1018 8.85088 11.0826 9.17969 11.3506L9.24316 11.4082L10.8838 13.0488L14.7568 9.17578Z" fill="black"/>
            </mask>
            <g mask="url(#mask0_1614_70992)">
            <rect x="-20.1074" y="-20.1075" width="64.215" height="64.215" fill="#323232"/>
            </g>
            </g>
            <defs>
            <clipPath id="clip0_1614_70992">
            <rect width="24" height="24" fill="white"/>
            </clipPath>
            </defs>
          </svg>
          ${items.length} ${skusHaveBeenAddedToCartLabel}`;
      }

      // Check what the modal close button should say
      const successCloseButton = this.successFooter?.querySelector('.rs-modal-close');
      if (document.querySelector('.order-details-page') && successCloseButton) {
        successCloseButton.textContent = 'Back to Order Details';
      }

      // Show success footer
      this.defaultFooter?.classList.add('hidden');
      this.successFooter?.classList.remove('hidden');

      this.classList.remove('rs-adding-to-cart');

      // Keep the success dialog open: update header count only now, and defer
      // full cart section refresh until the user closes the modal.
      this.pendingCartSectionRefresh = true;
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {source: 'quick-order-modal', skipSectionRefresh: true},
        })
      );
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (modalTitle && modalTitle instanceof HTMLElement) {
        modalTitle.textContent =
          (error instanceof Error && error.message) ||
          (typeof window.__t === 'function' ? window.__t('error_adding_to_cart') : 'Error adding to cart.');
      }
      this.classList.remove('rs-adding-to-cart');
    } finally {
      // Re-enable button
      if (this.addToCartButton instanceof HTMLButtonElement) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.removeAttribute('aria-busy');
      }
    }
  }
}

// Register the custom element
if (!customElements.get('quick-add-to-cart-modal')) {
  customElements.define('quick-add-to-cart-modal', QuickAddToCartModal);
}
